import { supabase } from "../lib/supabase";

export const bidsService = {
  // Place a new bid
  async placeBid(callId, bidAmount, autoGenerated = false) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Authentication required');

    const { data, error } = await supabase?.from('bids')?.insert({
        call_id: callId,
        buyer_id: user?.id,
        bid_amount: bidAmount,
        status: 'active',
        auto_generated: autoGenerated
      })?.select(`
        *,
        buyer:user_profiles!buyer_id(full_name, company_name),
        call:calls!call_id(insurance_type, customer_zip_code)
      `)?.single();

    if (error) throw error;
    return data;
  },

  // Get user's bids with call details
  async getUserBids(userId, status = null) {
    let query = supabase?.from('bids')?.select(`*,call:calls!call_id(id, insurance_type, call_quality, status, estimated_premium, customer_zip_code, created_at,publisher:user_profiles!publisher_id(full_name, company_name))`)?.eq('buyer_id', userId)?.order('created_at', { ascending: false });

    if (status) {
      query = query?.eq('status', status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  // Get all bids for a specific call
  async getCallBids(callId) {
    const { data, error } = await supabase?.from('bids')?.select(`*,buyer:user_profiles!buyer_id(full_name, company_name)`)?.eq('call_id', callId)?.order('bid_amount', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Update bid status
  async updateBidStatus(bidId, status) {
    const { data, error } = await supabase?.from('bids')?.update({ 
        status,
        updated_at: new Date()?.toISOString()
      })?.eq('id', bidId)?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Withdraw bid
  async withdrawBid(bidId) {
    return await this.updateBidStatus(bidId, 'withdrawn');
  },

  // Get bidding activity for dashboard
  async getBiddingActivity(userId, dateRange = 7) {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - dateRange);

    const { data, error } = await supabase?.from('bids')?.select(`*,call:calls!call_id(insurance_type, status)`)?.eq('buyer_id', userId)?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Get highest bid for a call
  async getHighestBid(callId) {
    const { data, error } = await supabase?.from('bids')?.select('*')?.eq('call_id', callId)?.eq('status', 'active')?.order('bid_amount', { ascending: false })?.limit(1);

    if (error) throw error;
    return data?.[0] || null;
  },

  // Get bid statistics for buyer
  async getBidStatistics(userId) {
    const { data, error } = await supabase?.from('bids')?.select('status, bid_amount')?.eq('buyer_id', userId);

    if (error) throw error;

    const stats = {
      total_bids: data?.length || 0,
      active_bids: data?.filter(bid => bid?.status === 'active')?.length || 0,
      won_bids: data?.filter(bid => bid?.status === 'won')?.length || 0,
      lost_bids: data?.filter(bid => bid?.status === 'lost')?.length || 0,
      total_spent: data
        ?.filter(bid => bid?.status === 'won')
        ?.reduce((sum, bid) => sum + (bid?.bid_amount || 0), 0) || 0
    };

    stats.win_rate = stats?.total_bids > 0 ? (stats?.won_bids / stats?.total_bids * 100) : 0;
    stats.avg_bid = stats?.total_bids > 0 ? 
      (data?.reduce((sum, bid) => sum + (bid?.bid_amount || 0), 0) / stats?.total_bids) : 0;

    return stats;
  },

  // Get competitive bidding data
  async getCompetitiveBidding(callId) {
    const { data, error } = await supabase?.from('bids')?.select('bid_amount, created_at, status')?.eq('call_id', callId)?.order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  }
};